/**
 * ============================================================================
 * [P7-01] ReferenceLoader - 레퍼런스 로더 모달
 *
 * 기능:
 * - 탭 전환 (저장된 글 / 트래킹)
 * - 검색 (디바운스 300ms)
 * - 카테고리 필터 (저장된 글 전용)
 * - 최근 사용 레퍼런스
 * - 레퍼런스 선택 시 콜백
 *
 * [2026-01-31] Phase 7 구현
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useState, useCallback, useEffect } from 'react'
import type { MouseEvent } from 'react'
import { useReferences } from '@/hooks'
import type { ReferenceItem, ReferenceSourceType } from '@/types'
import styles from './ReferenceLoader.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface ReferenceLoaderProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (item: ReferenceItem) => void
  onAnalyze?: (item: ReferenceItem) => void
  showAnalysisAction?: boolean
}

// ============================================================================
// 3. ReferenceLoader Component
// ============================================================================
export function ReferenceLoader({
  isOpen,
  onClose,
  onSelect,
  onAnalyze,
  showAnalysisAction = false,
}: ReferenceLoaderProps) {
  // ==========================================================================
  // 3-1. Hooks
  // ==========================================================================
  const {
    isLoading,
    loadSavedReferences,
    loadTrackingReferences,
    recentReferences,
    addToRecent,
    savedCategories,
  } = useReferences()

  // ==========================================================================
  // 3-2. Local State
  // ==========================================================================
  const [activeTab, setActiveTab] = useState<ReferenceSourceType>('saved')
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [items, setItems] = useState<ReferenceItem[]>([])

  // ==========================================================================
  // 3-3. 데이터 로드 (디바운스 300ms)
  // ==========================================================================
  useEffect(() => {
    if (!isOpen) return

    const timer = setTimeout(async () => {
      let result: ReferenceItem[] = []

      if (activeTab === 'saved') {
        result = await loadSavedReferences(search, category)
      } else {
        result = await loadTrackingReferences(search)
      }

      setItems(result)
    }, 300)

    // Cleanup: 타이머 정리 (메모리 누수 방지)
    return () => clearTimeout(timer)
  }, [
    activeTab,
    category,
    isOpen,
    loadSavedReferences,
    loadTrackingReferences,
    search,
  ])

  // ==========================================================================
  // 3-4. 탭 전환 핸들러
  // ==========================================================================
  const handleTabChange = useCallback((tab: ReferenceSourceType) => {
    setActiveTab(tab)
    setCategory('') // 탭 변경 시 카테고리 초기화
  }, [])

  // ==========================================================================
  // 3-5. 선택 핸들러
  // ==========================================================================
  const handleSelect = useCallback(
    (item: ReferenceItem) => {
      addToRecent(item)
      onSelect(item)
      onClose()
    },
    [addToRecent, onSelect, onClose]
  )

  const handleAnalyze = useCallback(
    (item: ReferenceItem, event?: MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation()
      if (!onAnalyze) return

      addToRecent(item)
      onAnalyze(item)
      onClose()
    },
    [addToRecent, onAnalyze, onClose]
  )

  // ==========================================================================
  // 3-6. 날짜 포맷 함수
  // ==========================================================================
  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
    })
  }

  // ==========================================================================
  // 3-7. Early Return (모달이 닫혀있으면 렌더링하지 않음)
  // ==========================================================================
  if (!isOpen) return null

  // ==========================================================================
  // 3-8. Render
  // ==========================================================================
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.referenceLoader}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ====================================================================
            헤더 영역
            ==================================================================== */}
        <div className={styles.loaderHeader}>
          <h3>레퍼런스 불러오기</h3>
          <button onClick={onClose} className={styles.closeBtn}>
            ×
          </button>
        </div>

        {/* ====================================================================
            탭 영역 (저장된 글 / 트래킹 포스트)
            ==================================================================== */}
        <div className={styles.loaderTabs}>
          <button
            className={`${styles.tabBtn} ${activeTab === 'saved' ? styles.active : ''}`}
            onClick={() => handleTabChange('saved')}
          >
            저장된 글
          </button>
          <button
            className={`${styles.tabBtn} ${activeTab === 'tracking' ? styles.active : ''}`}
            onClick={() => handleTabChange('tracking')}
          >
            트래킹 포스트
          </button>
        </div>

        {/* ====================================================================
            필터 영역 (검색, 카테고리)
            ==================================================================== */}
        <div className={styles.loaderFilters}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="검색..."
            className={styles.searchInput}
          />
          {activeTab === 'saved' && savedCategories.length > 0 && (
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={styles.categorySelect}
            >
              <option value="">전체 카테고리</option>
              {savedCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* ====================================================================
            최근 사용 레퍼런스 (localStorage에서 로드)
            ==================================================================== */}
        {recentReferences.length > 0 && (
          <div className={styles.recentSection}>
            <h4>📌 최근 사용</h4>
            <div className={styles.recentList}>
              {recentReferences.map((item) => (
                <button
                  key={item.id}
                  className={styles.recentItem}
                  onClick={() => handleSelect(item)}
                >
                  {item.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ====================================================================
            레퍼런스 목록
            ==================================================================== */}
        <div className={styles.loaderList}>
          {isLoading ? (
            <div className={styles.loading}>로딩 중...</div>
          ) : items.length === 0 ? (
            <div className={styles.empty}>검색 결과가 없습니다.</div>
          ) : (
            items.map((item) => (
              <div key={item.id} className={styles.refItem}>
                <div className={styles.refInfo}>
                  <span className={styles.refTitle}>{item.title}</span>
                  <span className={styles.refMeta}>
                    {item.category && `${item.category} | `}
                    {item.content.length}자 | {formatDate(item.createdAt)}
                  </span>
                  {item.metrics && (
                    <span className={styles.refMetrics}>
                      👁 {item.metrics.views} | ❤ {item.metrics.likes} | 💬{' '}
                      {item.metrics.comments}
                      {item.performanceScore !== undefined &&
                        ` | 점수 ${item.performanceScore}`}
                      {item.engagementRate !== undefined &&
                        ` | 참여 ${item.engagementRate}%`}
                    </span>
                  )}
                </div>
                <div className={styles.itemActions}>
                  {showAnalysisAction && onAnalyze && (
                    <button
                      className={styles.analyzeBtn}
                      onClick={(event) => handleAnalyze(item, event)}
                    >
                      분석
                    </button>
                  )}
                  <button
                    className={styles.selectBtn}
                    onClick={() => handleSelect(item)}
                  >
                    선택
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default ReferenceLoader
